import {stampMillis} from '@/lib/ReusableFunctions/Date';
import type {LightOMItem} from './types';

/** Mirrors `OM_LIGHT_FIELDS` in functions/filterMarkers/src/lightSources.ts (plus client-side `type`). */
export const LIGHT_OM_FIELDS = [
  'id',
  'name',
  'status',
  'location',
  'omBatchId',
  'linkedDocuments',
  'uploadedDocId',
  'generatedBy',
  'approvedBy',
  'createdOn',
  'editedOn',
  'deleted',
  'linkedSalesforceId',
  'createdCompetitiveBuildingId',
  'createdCompetitivePortfolioId',
  'type',
] as const satisfies readonly (keyof LightOMItem)[];

/** Every doc entering the OM store passes through this, so no heavy field can be found there by accident. */
export const toLightOMItem = (doc: Record<string, unknown>, extra: Partial<LightOMItem> = {}): LightOMItem => {
  const light: Record<string, unknown> = {};
  for (const field of LIGHT_OM_FIELDS) {
    if (doc[field] !== undefined) {
      light[field] = doc[field];
    }
  }
  return {...light, ...extra} as LightOMItem;
};

const STAMP_FIELDS = ['generatedBy', 'approvedBy', 'createdOn', 'editedOn'] as const;

/** JSON delivers stamp timestamps and `deleted` as ISO strings; the shelf's `Number(...)` sort needs Dates. */
export const reviveOMStamps = (item: LightOMItem): LightOMItem => {
  const out = {...item} as Record<(typeof STAMP_FIELDS)[number] | 'deleted', unknown>;
  for (const field of STAMP_FIELDS) {
    const stamp = out[field] as {timestamp?: unknown} | null | undefined;
    if (stamp && typeof stamp.timestamp === 'string') {
      out[field] = {...stamp, timestamp: new Date(stamp.timestamp)};
    }
  }
  if (typeof out.deleted === 'string') {
    out.deleted = new Date(out.deleted);
  }
  return out as LightOMItem;
};

/**
 * Newest-Wins Upsert (docs/contexts/om-review.md): the single write path into an OM store array, so
 * the initial fetch and the live listener can land in either order with no merge step. A stored copy
 * survives only if its `editedOn` is strictly newer; with no comparable stamp the incoming doc is taken.
 */
export const upsertLightOMItem = (
  items: LightOMItem[],
  doc: Record<string, unknown>,
  extra: Partial<LightOMItem> = {},
): LightOMItem[] => {
  const incoming = reviveOMStamps(toLightOMItem(doc, extra));
  const index = items.findIndex((item) => item.id === incoming.id);
  if (index === -1) return [...items, incoming];

  const existingAt = stampMillis(items[index].editedOn);
  const incomingAt = stampMillis(incoming.editedOn);
  if (existingAt !== null && incomingAt !== null && existingAt > incomingAt) return items;

  const next = [...items];
  next[index] = incoming;
  return next;
};

export const removeLightOMItem = (items: LightOMItem[], id: string): LightOMItem[] =>
  items.some((item) => item.id === id) ? items.filter((item) => item.id !== id) : items;
